import { useEffect, useMemo, useRef } from "react";
import { useIntl } from "react-intl";

// Project import
import useAuth from "./useAuth";
import { useTranslation } from "utils/locales/utilityFunctions";
import useEstatesAndMaps from "./useEstatesAndMaps";

const useActivityData = () => {
  // --- Hooks --- //
  const { translate } = useTranslation();
  const { activityTypes } = useAuth();
  const { locale } = useIntl();
  const { projects } = useEstatesAndMaps();

  // --- References and state --- //
  const langRef = useRef(locale);

  const activityList = useMemo(() => {
    return activityTypes;
  }, [activityTypes]);

  // --- Effects --- //
  useEffect(() => {
    langRef.current = locale;
  }, [locale]);

  // --- Functions --- //
  const getActivityName = (id: string) => {
    let name = "";
    if (id === "") return name;
    try {
      const activity = activityList![id];
      name = activity.translations[langRef.current];
      if (name === undefined) name = activity.translations.en;
    } catch (error) {
      console.warn("Activity not recognised", { id, error });
    }
    return name;
  };

  const getActivityGroup = (id: string) => {
     let group = "";
     if (id === "") return group;
     try {
        const activity = activityList![id];
        group = translate(`activity-group-${activity.group}-label`);
     } catch (error) {
        console.warn("Activity not recognised", { id, error })
     }
     return group;
  }

  const getActivityGroupId = (id: string) => {
    let groupId = "";
    if (id === "") return groupId;
    try {
      const activity = activityList![id];
      groupId = activity.group;
    } catch (error) {
      console.warn("Activity not recognised", { id, error });
    }
    return groupId;
  };

  const getActivityData = (id: string) => {
    let type = { id: "", group: "", label: "" , groupName: "" };
    if (id === "") return type;
    try {
      const name = getActivityName(id);
      const groupName = getActivityGroup(id);
      const groupId = getActivityGroupId(id);
      type = { id, group: groupId, label: name, groupName };

    } catch (error) {
      console.warn("Activity not recognised", { id, error });
    }
    return type;
  };

  const getActivityTypeList = (group?: string, availability?: "simple" | "extended" | "full") => {
    let list: { id: string; group: string; label: string }[] = [];
    try {
      Object.values(activityList!).forEach((activity: any) => {
        // Check for input filters
        if (group && activity.group !== group) return;
        if (availability) {
          if (availability === "simple" && activity.availability !== "simple") return;
          if (availability === "extended" && activity.availability === "removed") return;
        }
        // Add to list
        list.push({
          id: activity.id,
          group: activity.group,
          label: getActivityName(activity.id),
        });
      });
    } catch (error) {
      console.warn("Could not get activity list", { error });
    }
    return list
      .sort((a, b) => {
        const valueA = typeof a.label === "string" ? a.label : "";
        const valueB = typeof b.label === "string" ? b.label : "";
        return valueA.localeCompare(valueB);
      })
      .sort((a, b) => {
        const valueA = typeof a.group === "string" ? a.group : "";
        const valueB = typeof b.group === "string" ? b.group : "";
        return valueA.localeCompare(valueB);
      });
  };

  const getUserProjectRole = (userId: string, projectId: string) => {
    let role = "viewer";
    try {
      const project = Object.values(projects).find((proj) => proj.id === projectId);
      if (!project || !project.roles || !project.roles[userId]) return role;
      return project.roles[userId].role;
    } catch (error) {
      console.warn("Could not get user project role", { error });
    }
    return role;
  }

  return {
    activityList,
    getActivityName,
    getActivityGroup,
    getActivityData,
    getActivityTypeList,
    getUserProjectRole,
  };
};

export default useActivityData;
